import type { ResourceItem, ResourcesStorage, AIResourceSyncState } from '../core/types';
import { ensureFirestoreDb, type FirestoreServiceOptions } from './firestore-service';

type CreateFirestoreResourcesStorageOptions = FirestoreServiceOptions;

interface ResourceDocData extends Record<string, unknown> {
  type?: unknown;
  title?: unknown;
  description?: unknown;
  url?: unknown;
  storagePath?: unknown;
  fileName?: unknown;
  mimeType?: unknown;
  sizeBytes?: unknown;
  isVisible?: unknown;
  order?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
  createdBy?: unknown;
}

interface AIStateDocData extends Record<string, unknown> {
  resourceId?: unknown;
  status?: unknown;
  attempt?: unknown;
  chunkCount?: unknown;
  contentChars?: unknown;
  lastError?: unknown;
  updatedAt?: unknown;
  indexedAt?: unknown;
}

function toMillis(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (raw && typeof raw === 'object' && 'toMillis' in (raw as { toMillis?: unknown }) && typeof (raw as { toMillis?: () => number }).toMillis === 'function') {
    return (raw as { toMillis: () => number }).toMillis();
  }
  return 0;
}

function normalizeHttpUrl(url: string): string {
  const raw = String(url || '').trim();
  if (!raw) throw new Error('Enter a URL');
  const next = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  const parsed = new URL(next);
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new Error('Only http(s) URLs are allowed');
  }
  const host = String(parsed.hostname || '').trim().toLowerCase();
  const isIpv4 = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host) && host.split('.').every((p) => Number(p) >= 0 && Number(p) <= 255);
  const isDomain = (() => {
    if (!host || host.endsWith('.') || host.length > 253) return false;
    const labels = host.split('.');
    if (labels.length < 2) return false;
    if (!labels.every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label))) return false;
    const tld = labels[labels.length - 1];
    return /^[a-z]{2,63}$/i.test(tld);
  })();
  if (!host || (!isIpv4 && !isDomain)) {
    throw new Error('Enter a valid URL');
  }
  if (host === 'localhost' || host.endsWith('.local')) {
    throw new Error('Local network URLs are not allowed');
  }
  if (isIpv4) {
    const [a, b] = host.split('.').map((p) => Number(p));
    const privateRange =
      a === 10 ||
      a === 127 ||
      a === 0 ||
      (a === 169 && b === 254) ||
      (a === 192 && b === 168) ||
      (a === 172 && b >= 16 && b <= 31);
    if (privateRange) throw new Error('Private/local IP URLs are not allowed');
  }
  return parsed.toString();
}

function getCurrentUid(): string {
  const fb = typeof window !== 'undefined' ? (window as { firebase?: unknown }).firebase : null;
  const authFn = fb && typeof (fb as { auth?: () => unknown }).auth === 'function' ? (fb as { auth: () => { currentUser?: { uid?: string } | null } }).auth : null;
  const uid = String(authFn?.()?.currentUser?.uid || '').trim();
  if (!uid) throw new Error('Sign in required');
  return uid;
}

function validateFile(file: File): void {
  const MAX_BYTES = 25 * 1024 * 1024;
  if (file.size > MAX_BYTES) throw new Error('File too large (max 25MB)');
  const lower = String(file.name || '').toLowerCase();
  const ok = /\.(pdf|ppt|pptx|doc|docx|xls|xlsx|csv|txt|md|zip|7z|png|jpg|jpeg|gif|svg|webp|mp4|webm)$/.test(lower);
  if (!ok) throw new Error('Unsupported file type');
}

function sortResources(items: ResourceItem[]): ResourceItem[] {
  return items.slice().sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return (a.createdAt ?? 0) - (b.createdAt ?? 0);
  });
}

function mapDoc(doc: { id: string; data(): ResourceDocData }): ResourceItem {
  const d = doc.data() || {};
  return {
    id: doc.id,
    type: d.type === 'file' ? 'file' : 'url',
    title: String(d.title ?? 'Untitled resource'),
    description: d.description ? String(d.description) : undefined,
    url: d.url ? String(d.url) : undefined,
    storagePath: d.storagePath ? String(d.storagePath) : undefined,
    fileName: d.fileName ? String(d.fileName) : undefined,
    mimeType: d.mimeType ? String(d.mimeType) : undefined,
    sizeBytes: Number.isFinite(Number(d.sizeBytes)) ? Number(d.sizeBytes) : undefined,
    isVisible: d.isVisible === true,
    order: Number.isFinite(Number(d.order)) ? Number(d.order) : 0,
    createdAt: toMillis(d.createdAt),
    updatedAt: toMillis(d.updatedAt),
    createdBy: d.createdBy ? String(d.createdBy) : undefined
  };
}

function getStorageCompat(): {
  ref(): { child(path: string): { put(file: File): Promise<unknown>; getDownloadURL(): Promise<string>; delete(): Promise<void> } };
} {
  const fb = typeof window !== 'undefined' ? (window as { firebase?: unknown }).firebase : null;
  if (!fb || typeof (fb as { storage?: () => unknown }).storage !== 'function') {
    throw new Error('Firebase Storage is not available');
  }
  return (fb as { storage: () => { ref(): { child(path: string): { put(file: File): Promise<unknown>; getDownloadURL(): Promise<string>; delete(): Promise<void> } } } }).storage();
}

export function createFirestoreResourcesStorage(opts: CreateFirestoreResourcesStorageOptions): ResourcesStorage {
  const cache = new Map<string, ResourceItem[]>();
  const aiStateCache = new Map<string, Record<string, AIResourceSyncState>>();

  async function getCollection(presId: string): Promise<{
    doc(id?: string): {
      id: string;
      set(data: Record<string, unknown>, options?: { merge?: boolean }): Promise<void>;
      update(data: Record<string, unknown>): Promise<void>;
      delete(): Promise<void>;
      get(): Promise<{ exists: boolean; data(): Record<string, unknown> }>;
    };
    onSnapshot(cb: (snap: { docs: Array<{ id: string; data(): Record<string, unknown> }> }) => void, errCb?: (err: unknown) => void): () => void;
  }> {
    const db = await ensureFirestoreDb(opts);
    return (db as unknown as {
      collection(path: string): { doc(id: string): { collection(path: string): unknown } };
    })
      .collection('presentations')
      .doc(String(presId))
      .collection('resources') as {
      doc(id?: string): {
        id: string;
        set(data: Record<string, unknown>, options?: { merge?: boolean }): Promise<void>;
        update(data: Record<string, unknown>): Promise<void>;
        delete(): Promise<void>;
        get(): Promise<{ exists: boolean; data(): Record<string, unknown> }>;
      };
      onSnapshot(cb: (snap: { docs: Array<{ id: string; data(): Record<string, unknown> }> }) => void, errCb?: (err: unknown) => void): () => void;
    };
  }

  async function getAIStateCollection(presId: string): Promise<{
    onSnapshot(cb: (snap: { docs: Array<{ id: string; data(): Record<string, unknown> }> }) => void, errCb?: (err: unknown) => void): () => void;
  }> {
    const db = await ensureFirestoreDb(opts);
    return (db as unknown as {
      collection(path: string): { doc(id: string): { collection(path: string): unknown } };
    })
      .collection('presentations')
      .doc(String(presId))
      .collection('aiState') as {
      onSnapshot(cb: (snap: { docs: Array<{ id: string; data(): Record<string, unknown> }> }) => void, errCb?: (err: unknown) => void): () => void;
    };
  }

  function mapAIStateDoc(doc: { id: string; data(): AIStateDocData }): AIResourceSyncState {
    const d = doc.data() || {};
    const statusRaw = String(d.status || '').trim().toLowerCase();
    const status: AIResourceSyncState['status'] =
      statusRaw === 'pending' || statusRaw === 'indexed' || statusRaw === 'error' || statusRaw === 'deleted' || statusRaw === 'stale'
        ? statusRaw
        : 'pending';
    return {
      resourceId: String(d.resourceId || doc.id),
      status,
      attempt: Number.isFinite(Number(d.attempt)) ? Number(d.attempt) : undefined,
      chunkCount: Number.isFinite(Number(d.chunkCount)) ? Number(d.chunkCount) : undefined,
      contentChars: Number.isFinite(Number(d.contentChars)) ? Number(d.contentChars) : undefined,
      lastError: d.lastError ? String(d.lastError) : undefined,
      updatedAt: toMillis(d.updatedAt),
      indexedAt: toMillis(d.indexedAt)
    };
  }

  return {
    getResources(presId: string): ResourceItem[] {
      return cache.get(String(presId)) ?? [];
    },
    getAIState(presId: string): Record<string, AIResourceSyncState> {
      return aiStateCache.get(String(presId)) ?? {};
    },

    subscribeResources(presId, cb, onError) {
      let closed = false;
      let unsub: (() => void) | null = null;
      const handleSnapshot = (snap: { docs: Array<{ id: string; data(): Record<string, unknown> }> }) => {
        const items = sortResources((snap.docs || []).map((d) => mapDoc(d as { id: string; data(): ResourceDocData })));
        cache.set(String(presId), items);
        cb(items.slice());
      };
      const subscribeVisibleOnly = () => {
        ensureFirestoreDb(opts)
          .then((db) => {
            if (closed) return;
            const dbAny = db as unknown as {
              collection(path: string): {
                doc(id: string): {
                  collection(path: string): {
                    where(fieldPath: string, opStr: '==', value: unknown): {
                      onSnapshot(
                        cb: (snap: { docs: Array<{ id: string; data(): Record<string, unknown> }> }) => void,
                        errCb?: (err: unknown) => void
                      ): () => void;
                    };
                  };
                };
              };
            };
            const query = dbAny
              .collection('presentations')
              .doc(String(presId))
              .collection('resources')
              .where('isVisible', '==', true);
            unsub = query.onSnapshot(
              (snap) => handleSnapshot(snap),
              (err) => onError?.(err instanceof Error ? err : new Error(String(err)))
            );
          })
          .catch((err) => onError?.(err instanceof Error ? err : new Error(String(err))));
      };
      getCollection(presId)
        .then((col) => {
          if (closed) return;
          unsub = col.onSnapshot(
            (snap) => handleSnapshot(snap),
            (err) => {
              const code = String((err as { code?: unknown } | null)?.code || '');
              if (code === 'permission-denied') {
                if (unsub) {
                  unsub();
                  unsub = null;
                }
                subscribeVisibleOnly();
                return;
              }
              onError?.(err instanceof Error ? err : new Error(String(err)));
            }
          );
        })
        .catch((err) => onError?.(err instanceof Error ? err : new Error(String(err))));
      return () => {
        closed = true;
        if (unsub) unsub();
      };
    },

    subscribeAIState(presId, cb, onError) {
      let closed = false;
      let unsub: (() => void) | null = null;
      getAIStateCollection(presId)
        .then((col) => {
          if (closed) return;
          unsub = col.onSnapshot(
            (snap) => {
              const next: Record<string, AIResourceSyncState> = {};
              (snap.docs || []).forEach((doc) => {
                const mapped = mapAIStateDoc(doc as { id: string; data(): AIStateDocData });
                next[mapped.resourceId] = mapped;
              });
              aiStateCache.set(String(presId), next);
              cb({ ...next });
            },
            (err) => {
              const code = String((err as { code?: unknown } | null)?.code || '');
              if (code === 'permission-denied') {
                aiStateCache.set(String(presId), {});
                cb({});
                return;
              }
              onError?.(err instanceof Error ? err : new Error(String(err)));
            }
          );
        })
        .catch((err) => onError?.(err instanceof Error ? err : new Error(String(err))));
      return () => {
        closed = true;
        if (unsub) unsub();
      };
    },

    async addUrl(presId, input) {
      const col = await getCollection(presId);
      const uid = getCurrentUid();
      const normalized = normalizeHttpUrl(input.url);
      const existing = cache.get(String(presId)) ?? [];
      const order = existing.length ? Math.max(...existing.map((i) => i.order || 0)) + 1 : 0;
      const doc = col.doc();
      const description = String(input.description || '').trim();
      await doc.set({
        type: 'url',
        title: String(input.title || normalized).trim() || normalized,
        url: normalized,
        isVisible: input.isVisible === true,
        order,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        createdBy: uid,
        ...(description ? { description } : {})
      });
    },

    async addFile(presId, file, input) {
      if (!(file instanceof File)) throw new Error('Invalid file');
      const uid = getCurrentUid();
      validateFile(file);
      const col = await getCollection(presId);
      const existing = cache.get(String(presId)) ?? [];
      const order = existing.length ? Math.max(...existing.map((i) => i.order || 0)) + 1 : 0;
      const doc = col.doc();
      const cleanName = file.name.replace(/[^\w.-]/g, '_');
      const storagePath = `presentations/${String(presId)}/resources/${doc.id}/${cleanName}`;
      const storage = getStorageCompat();
      const fileRef = storage.ref().child(storagePath);
      await fileRef.put(file);
      const url = await fileRef.getDownloadURL();
      const description = String(input?.description || '').trim();
      const mimeType = String(file.type || '').trim();
      await doc.set({
        type: 'file',
        title: String(input?.title || file.name).trim() || file.name,
        url,
        storagePath,
        fileName: file.name,
        ...(description ? { description } : {}),
        ...(mimeType ? { mimeType } : {}),
        sizeBytes: Number(file.size || 0),
        isVisible: input?.isVisible === true,
        order,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        createdBy: uid
      });
    },

    async updateResource(presId, resourceId, patch) {
      const col = await getCollection(presId);
      const doc = col.doc(String(resourceId));
      const payload: Record<string, unknown> = { updatedAt: Date.now() };
      if (patch.title !== undefined) payload.title = String(patch.title || '').trim();
      if (patch.description !== undefined) payload.description = String(patch.description || '').trim();
      if (patch.isVisible !== undefined) payload.isVisible = patch.isVisible === true;
      if (patch.order !== undefined && Number.isFinite(Number(patch.order))) payload.order = Number(patch.order);
      await doc.update(payload);
    },

    async removeResource(presId, resourceId) {
      const col = await getCollection(presId);
      const doc = col.doc(String(resourceId));
      const snap = await doc.get();
      const data = snap.exists ? (snap.data() as ResourceDocData) : null;
      const storagePath = data?.storagePath ? String(data.storagePath) : '';
      if (storagePath) {
        try {
          const storage = getStorageCompat();
          await storage.ref().child(storagePath).delete();
        } catch {
          // Continue and remove doc even if file cleanup fails.
        }
      }
      await doc.delete();
    },

    async reorderResources(presId, orderedIds) {
      const col = await getCollection(presId);
      await Promise.all(
        orderedIds.map((id, idx) =>
          col
            .doc(String(id))
            .update({
              order: idx,
              updatedAt: Date.now()
            })
        )
      );
    }
  };
}
